import { useEffect, useState } from "react";
import dailyFilterDefault from '@/pages/dashboard/filterPageComponents/overview/dailyFilterDefault';
import { formatFilterValues, formatSingleTime } from '@/pages/dashboard/utils/utils';
import { getDefaultUtcDateValue } from "@/pages/dashboard/utils/dateUtils";
import { filterTrendEventListByExcludedEventTypes } from '@/pages/dashboard/components/EventMetricTrend/trendEventListFilters';
import { getDateEventTypeList } from "../../services/event";
import { mergeTrendEventListConsecutiveDays } from "./mergeTrendEventListConsecutiveDays";
import { expandKpiTrendRangeToMinDays } from '@/pages/dashboard/pages/overview/kpiOverview/components/KpiEventMetricTrendService/_utils';

export interface IOverviewEventDataOptions {
  /**
   * 为 true 时：对 trend 按日列表做「连续自然日同名事件」合并（仅 KPI 总览等显式开启）。
   * 默认 false，避免影响其它使用 date_event_list 的页面。
   */
  mergeConsecutiveTrendDays?: boolean;
  /**
   * 按 event_type 排除事件（trim 后与下列任一字段全等即排除：字符串 event_type，或对象上的 name_cn / name_en / name）；在合并连续日之前执行。
   */
  excludeEventTypes?: string[];
  /**
   * 为 true 时不传 show_event_num / event_deduplication，由接口侧默认或全量返回。
   * 未传 options 的旧调用方仍固定传 show_event_num: 3、event_deduplication: true。
   */
  removeEventNumLimit?: boolean;
  /**
   * 传入时才带 filter_event_date_field；旧调用方不传，沿用后端默认日期字段。
   */
  filterEventDateField?: string;
  /**
   * 日粒度短区间拉满的最小天数（如 ROAS 趋势需 30 天）。
   * 与 KpiEventMetricTrendService 的 minTrendRangeDays 对齐，
   * 使事件时间范围与趋势图一致。
   */
  minTrendRangeDays?: number;
}

/**
 * 按照接口区分为不同model
 * hook名称规范：use+接口名称+Data
 * @param props 父页面所有props
 * @param props: timeZone 时区信息，realtime特有
 * @param props: dispatch dva  dispatch方法
 * @returns
 * 获取数据的逻辑：dva中有数据则取dva中的数据，否则自己通过接口请求
 */

const useDailyMetricsModel = (
  gameCode: string,
  formValue: any,
  filterLoading: boolean,
  api?: string,
  options?: IOverviewEventDataOptions,
): [any[], boolean, boolean] => {
  const mergeConsecutiveTrendDays = Boolean(
    options?.mergeConsecutiveTrendDays,
  );
  const excludeEventTypes = options?.excludeEventTypes;
  const removeEventNumLimit = Boolean(options?.removeEventNumLimit);
  const filterEventDateField = options?.filterEventDateField;
  const minTrendRangeDays = options?.minTrendRangeDays;
  const formVal = formValue || dailyFilterDefault;
  const [eventData, setEventData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  // 点击筛选器的时候
  useEffect(() => {
    if (!formValue) return;
    if (!filterLoading) {
      const { filterValue, diff } = formValue || {};
      const type = formVal?.timeType || 'Day';
      let period: 'monthly' | 'weekly' | 'daily' = 'daily';
      if (type === 'Month') {
        period = 'monthly';
      } else if (type === 'Week') {
        period = 'weekly';
      }
      const { rangeTime, ...filters } = filterValue || {};
      const defaultTimeRange = getDefaultUtcDateValue(formVal?.timeType,rangeTime);
      const rangeTimeArr = diff ? defaultTimeRange : formatSingleTime(rangeTime, type);
      let [start, end] = rangeTimeArr;

      // 当 minTrendRangeDays 有值时，与趋势图对齐：日粒度短区间拉满到指定天数
      if (
        typeof minTrendRangeDays === 'number' &&
        minTrendRangeDays > 0 &&
        period === 'daily' &&
        start &&
        end
      ) {
        const expanded = expandKpiTrendRangeToMinDays(
          start,
          end,
          period,
          minTrendRangeDays,
        );
        start = expanded.start_time;
        end = expanded.end_time;
      }

      const params = {
        game_code: gameCode,
        filters: formatFilterValues(filters),
        start_time: start,
        end_time: end,
        ...(!removeEventNumLimit
          ? {
            show_event_num: 3,
            event_deduplication: true,
          }
          : {}),
        ...(filterEventDateField
          ? { filter_event_date_field: filterEventDateField }
          : {}),
      };
      requestDateEventTypeList({ ...params, period });
    }
  }, [
    JSON.stringify(formVal),
    filterLoading,
    gameCode,
    api,
    mergeConsecutiveTrendDays,
    removeEventNumLimit,
    filterEventDateField,
    minTrendRangeDays,
    JSON.stringify(excludeEventTypes ?? []),
  ]);

  // 查询事件数量和事件分析详情
  const requestDateEventTypeList = (params: any) => {
    setLoading(true);
    setIsError(false);
    return getDateEventTypeList(params, api).then((res) => {
      const rawList = res?.list || [];
      const afterTypeFilter = filterTrendEventListByExcludedEventTypes(
        rawList,
        excludeEventTypes,
      );
      setEventData(
        mergeConsecutiveTrendDays
          ? mergeTrendEventListConsecutiveDays(afterTypeFilter)
          : afterTypeFilter,
      );
      setIsError(false);
      setLoading(false);
    }).catch(e => {
      console.log('requestDateEventTypeList-error', e);
      setIsError(true);
      setLoading(false);
    });
  };

  // return {
  // eventData,
  // 事件分析接口请求失败应该不能影响页面的展示才对，所以这里先不返回loading和error
  // loading,
  // error,
  // }
  return [eventData, loading, isError];
};

export default useDailyMetricsModel;
